import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Chapter } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "Hotel Leadership",
  "Food & Beverage",
  "Restaurant Management",
  "Culinary",
  "Pastry",
  "Beverage & Sommelier",
  "Guest Experience",
  "Sales & Marketing",
  "Corporate Hospitality",
];

const FIELDS = [
  { name: "full_name", label: "Full Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone / WhatsApp" },
  { name: "location", label: "Current Location" },
  { name: "nationality", label: "Nationality" },
  { name: "current_position", label: "Current Position" },
  { name: "desired_position", label: "Desired Position" },
  { name: "years_experience", label: "Years of Experience" },
  { name: "linkedin", label: "LinkedIn Profile" },
];

const Candidates = () => {
  const [form, setForm] = useState({});
  const [cv, setCv] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("CV must be under 4MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCv({ name: file.name, data: reader.result.split(",")[1] });
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form };
      if (cv) {
        payload.cv_filename = cv.name;
        payload.cv_data = cv.data;
      }
      const res = await fetch(`${API}/candidates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success("Profile submitted. Thank you — we will be in touch.");
      setForm({});
      setCv(null);
      e.target.reset();
    } catch {
      toast.error("Something went wrong. Please email info@br-hospitality.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="candidates" data-testid="candidates-section" className="bg-sand py-28 lg:py-40">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-5">
            <Reveal>
              <Chapter number="10" label="For Candidates" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.08] text-ink">
                Your Next Hospitality <span className="italic text-bronze">Chapter</span>
              </h2>
              <p className="mt-7 text-sm text-ink/60 leading-relaxed">
                BR Hospitality works with ambitious hospitality professionals seeking meaningful
                career opportunities with quality-driven brands. We are interested in connecting
                with leaders and specialists who demonstrate professionalism, integrity, genuine
                hospitality and a commitment to excellence.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <span
                    key={a}
                    className="text-[10px] tracking-[0.18em] uppercase text-ink/60 border border-ink/20 px-3 py-1.5"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                data-testid="candidate-profile-form"
                onSubmit={submit}
                className="border border-ink/10 bg-ivory p-8 lg:p-12"
              >
                <p className="eyebrow mb-10">Submit Your CV</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  {FIELDS.map((f) => (
                    <div key={f.name} className={f.name === "linkedin" ? "sm:col-span-2" : ""}>
                      <label className="field-label" htmlFor={`cand-${f.name}`}>
                        {f.label}
                        {f.required ? " *" : ""}
                      </label>
                      <input
                        id={`cand-${f.name}`}
                        data-testid={`candidate-field-${f.name.replace(/_/g, "-")}`}
                        type={f.type || "text"}
                        required={f.required}
                        onChange={set(f.name)}
                        className="field-input"
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="cand-cv">
                      CV Upload (PDF / DOC, max 4MB)
                    </label>
                    <input
                      id="cand-cv"
                      data-testid="candidate-field-cv-upload"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={onFile}
                      className="block w-full text-sm text-ink/60 file:mr-4 file:py-2.5 file:px-5 file:border file:border-ink/20 file:bg-transparent file:text-[10px] file:tracking-[0.2em] file:uppercase file:text-ink hover:file:border-bronze file:transition-colors file:duration-500 file:cursor-pointer"
                    />
                    {cv && (
                      <p className="mt-2 text-xs text-bronze" data-testid="candidate-cv-attached">
                        Attached: {cv.name}
                      </p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="cand-message">
                      Message
                    </label>
                    <textarea
                      id="cand-message"
                      data-testid="candidate-field-message"
                      rows={4}
                      onChange={set("message")}
                      className="field-input resize-none"
                    />
                  </div>
                </div>
                <button
                  data-testid="candidate-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-10 w-full sm:w-auto text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-ink text-ivory hover:bg-bronze hover:text-ink transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? "Submitting…" : "Submit Profile"}
                </button>
                <p className="mt-6 text-[11px] text-ink/40 leading-relaxed" data-testid="candidate-privacy-note">
                  Candidate information is handled confidentially and used only for relevant
                  hospitality recruitment opportunities.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Candidates;
